import React from 'react';
import { Box, Button, Card, CardContent, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { adminColors, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminBottomBannerSx,
  adminCardContentSx,
  adminCheckboxSx,
  adminCombineSx,
  adminDividerSx,
  adminIconTitleSx,
  adminInlineControlLabelSx,
  adminSurfaceSx,
} from '../../styles/adminComponentStyles';

type LottoSetupCardProps = {
  lottoEnabled: boolean;
  onLottoEnabledChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxIcon: React.ReactNode;
  checkboxCheckedIcon: React.ReactNode;
  username: string;
  lotteryRetailerId?: string | null;
  password: string;
  passwordVisible: boolean;
  passwordRevealing: boolean;
  onEditLottoPassword: () => void;
  onRevealLottoPassword: () => void;
  onScratchersBulkSync: () => void;
  scratcherBulkSyncLoading: boolean;
  lottoLastSeenLabel: string;
  lottoPasswordLastRotatedLabel: string;
};

const LottoSetupCard: React.FC<LottoSetupCardProps> = ({
  lottoEnabled,
  onLottoEnabledChange,
  checkboxIcon,
  checkboxCheckedIcon,
  username,
  lotteryRetailerId,
  password,
  passwordVisible,
  passwordRevealing,
  onEditLottoPassword,
  onRevealLottoPassword,
  onScratchersBulkSync,
  scratcherBulkSyncLoading,
  lottoLastSeenLabel,
  lottoPasswordLastRotatedLabel,
}) => (
  <Card
    elevation={0}
    sx={adminSurfaceSx}
  >
    <CardContent sx={adminCombineSx(adminCardContentSx, { pb: 0 })}>
      <Box sx={{ backgroundColor: '#F8FBFF', mx: -2, mt: -2, px: 2, pt: 1, pb: 1, mb: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 0 }}>
          <Stack direction="row" alignItems="center" sx={adminIconTitleSx}>
            <Box className="AdminIconTitle-icon">
              <img src="/Icons/LottoSetup.svg" width={22} height={22} alt="Lotto setup" />
            </Box>
            <Typography className="AdminIconTitle-title" sx={{ color: `${adminColors.primary} !important` }}>
              Lotto Setup
            </Typography>
            <Tooltip title="Edit credentials">
              <IconButton
                aria-label="Edit lotto credentials"
                size="small"
                onClick={onEditLottoPassword}
                sx={{ color: adminColors.primary }}
              >
                <Icon icon="fluent:edit-24-regular" width="18" height="18" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={lottoEnabled}
                  onChange={onLottoEnabledChange}
                  icon={checkboxIcon}
                  checkedIcon={checkboxCheckedIcon}
                  sx={adminCheckboxSx}
                />
              }
              label="Is Enabled"
              sx={adminInlineControlLabelSx}
            />
          </Stack>
        </Stack> 
      </Box>

      <Stack spacing={1.25}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...adminTypography.helper, color: '#222b4599', display: 'block' }}>
            User Name
          </Typography>
          <Typography
            sx={{ ...adminTypography.bodyStrong, color: adminColors.text, wordBreak: 'break-word', overflowWrap: 'anywhere', minHeight: '22px', display: 'flex',  alignItems: 'center', }}
          >
            {username || '----------'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ ...adminTypography.helper, color: '#222b4599', display: 'block' }}>
            Lottery Retailer ID
          </Typography>
          <Typography
            sx={{ ...adminTypography.bodyStrong, color: adminColors.text, wordBreak: 'break-word', overflowWrap: 'anywhere', minHeight: '22px', display: 'flex',  alignItems: 'center', }}
          >
            {lotteryRetailerId || '----------'}
          </Typography>
        </Box>

        <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1, justifyContent: 'space-between', minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...adminTypography.helper, color: '#222b4599', display: 'block' }}>
              Password
            </Typography>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              {passwordVisible ? (password || '----------') : (username ? '••••••••••' : '----------')}

              <Tooltip title={passwordVisible ? 'Hide password' : 'Show password'}>
                <span>
                  <IconButton
                    aria-label={passwordVisible ? 'Hide lotto password' : 'Show lotto password'}
                    size="small"
                    onClick={onRevealLottoPassword}
                    disabled={!username || passwordRevealing}
                    sx={{ color: adminColors.primary, p: '0', mx:'4px' }}
                  >
                    {passwordRevealing ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Icon
                        icon={passwordVisible ? 'fluent:eye-off-24-regular' : 'fluent:eye-24-regular'}
                        width="22"
                        height="22"
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Typography>
          </Box> 
        </Box>

        </Stack>

        {lottoEnabled && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="text"
              size="small"
              onClick={onScratchersBulkSync}
              disabled={scratcherBulkSyncLoading}
              startIcon={
                scratcherBulkSyncLoading
                  ? <CircularProgress size={16} />
                  : <Icon icon="mdi:cloud-download-outline" width="16" height="16" />
              }
              sx={{
                alignSelf: 'flex-start',
                backgroundColor: adminColors.primarySoft,
                color: adminColors.primary,
                fontSize: 12,
                fontWeight: 700,
                minHeight: 32,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: adminColors.primarySoft,
                },
              }}
            >
              {scratcherBulkSyncLoading
                ? 'Fetching scratchers...'
                : 'Backfill Scratcher Data'}
            </Button>
          </Stack>
        )}
      </Stack>
    </CardContent>

    {
      lottoLastSeenLabel && <Box sx={adminCombineSx(adminBottomBannerSx, {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: 'space-between',
        mt: 2,
        '& span.last-seen-value': {
          color: '#2c6ca0',
          fontWeight: 600,
        },
      })}>
        <Typography className="AdminBottomBanner-text" sx={{ textAlign: 'left' }}>
          <span className="last-seen-dot" style={{ display: 'inline-block', height: '8px', width: '8px', background: '#66b005', borderRadius: '50%' }} /> Last Seen: <span className='last-seen-value'>{lottoLastSeenLabel}</span>
        </Typography>
        <Typography className="AdminBottomBanner-text" sx={{ marginLeft: 'auto', textAlign: 'right' }}>
          Password Last Rotated: <span className='last-seen-value'>{lottoPasswordLastRotatedLabel}</span>
        </Typography> 
      </Box>
    }

  </Card>
);

export default React.memo(LottoSetupCard);
